import React, { useState, useEffect, useRef } from 'react';
import SEO from '../../components/common/SEO';
import { Link } from 'react-router-dom';
import { COMPANY } from '../../config/company';
import { DNAStrand } from '../../components/common/PharmaAnimations';

/* ─── Scroll-reveal wrapper ───────────────────────────────── */
function FadeIn({ children, delay = 0, direction = 'up', threshold = 0.12 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVis(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  const t = { up: 'translateY(32px)', down: 'translateY(-32px)', left: 'translateX(-36px)', right: 'translateX(36px)', none: 'none' };
  return (
    <div ref={ref} style={{
      opacity: vis ? 1 : 0,
      transform: vis ? 'none' : t[direction],
      transition: `opacity 0.65s ${delay}ms cubic-bezier(.4,0,.2,1), transform 0.65s ${delay}ms cubic-bezier(.4,0,.2,1)`,
    }}>
      {children}
    </div>
  );
}

/* ─── Data ─────────────────────────────────────────────────── */
const JOURNEY = [
  {
    year: '2020',
    title: 'The Beginning',
    label: 'Founding Vision',
    desc: 'PRASI LABS is born in Hyderabad — a bold idea to make pharmaceutical impurity standards accessible, transparent, and custom-built for global pharma.',
    icon: '🔬',
    color: '#7c3aed',
    bg: '#f5f3ff',
  },
  {
    year: '2021',
    title: 'Officially Incorporated',
    label: 'CIN · U24290TG2021PTC148865',
    desc: 'PRASI LABS Pvt. Ltd. formally registered. MSME certificate (UDYAM-TS-09-0012881) and Letter of Undertaking (LUT) secured for zero-rated global exports.',
    icon: '📋',
    color: '#1d4ed8',
    bg: '#eff6ff',
  },
  {
    year: '2022',
    title: 'Catalog Goes Live',
    label: '500+ Standards Published',
    desc: 'First digital catalog launched with 500+ drug impurities, reference standards, and metabolites. IEC code obtained; first shipments to EU and US clients.',
    icon: '📦',
    color: '#0891b2',
    bg: '#ecfeff',
  },
  {
    year: '2022',
    title: 'First Global Shipments',
    label: 'EU · USA · Japan · Australia',
    desc: 'Products reach pharma majors across four continents. Ajanta Pharma, Biocon, Syngene, and Aurigene join as early Indian partners.',
    icon: '✈️',
    color: '#059669',
    bg: '#f0fdf4',
  },
  {
    year: '2023',
    title: 'ISO Accreditations',
    label: 'Quality Certified',
    desc: 'Achieved ISO 9001:2015, ISO 17025, ISO 17034, ISO 14001, and GMP compliance — validating world-class quality systems and laboratory competence.',
    icon: '🏆',
    color: '#d97706',
    bg: '#fffbeb',
  },
  {
    year: '2023',
    title: 'Catalog Hits 1,000+',
    label: 'Scale & Diversity',
    desc: 'Portfolio expands beyond 1,000 items: ICH Q3A/Q3B impurities, genotoxic impurities (ICH M7), chiral standards, and API reference materials.',
    icon: '📈',
    color: '#1d4ed8',
    bg: '#eff6ff',
  },
  {
    year: '2024',
    title: 'Lab Infrastructure Upgrade',
    label: 'mg → kg Scale Capability',
    desc: 'New synthesis wing commissioned with Jacketed Reactors, Hydrogenator, Rota Vapor, and Glass Vacuum Distillation enabling kg-scale custom production.',
    icon: '🏗️',
    color: '#7c3aed',
    bg: '#f5f3ff',
  },
  {
    year: '2025',
    title: 'Stable Isotopes & Beyond',
    label: '1,500+ Products · 50+ Global Clients',
    desc: 'Complete ²H, ¹³C, ¹⁵N stable isotope library added. Serving 50+ pharma companies and CROs worldwide with 24-hour quote turnaround.',
    icon: '⚗️',
    color: '#c4241a',
    bg: '#fff0f0',
  },
];

const ACCREDITATIONS = [
  { src: '/about/accreditations/made-in-india.jpg', alt: 'Made in India' },
  { src: '/about/accreditations/iso-17025.jpg', alt: 'ISO/IEC 17025' },
  { src: '/about/accreditations/iso-17038.png', alt: 'ISO 17038' },
  { src: '/about/accreditations/gmp.png', alt: 'GMP Compliance' },
  { src: '/about/accreditations/iso-14001.png', alt: 'ISO 14001:2015' },
  { src: '/about/accreditations/iso-45001.png', alt: 'ISO 45001:2015' },
  { src: '/about/accreditations/iso-17034.png', alt: 'ISO 17034' },
  { src: '/about/accreditations/iso-9001.png', alt: 'ISO 9001:2015' },
];

const TEAM = [
  { photo: '/about/team/Rahul.jpg', name: 'Rahul Mishra', role: 'Founder & CEO', bio: 'Rahul leads the company with a vision for innovation and growth, driving success through strategic leadership and pharma expertise.', linkedin: 'https://www.linkedin.com/in/rahulmishra89/', email: 'info@prasilabs.com', whatsapp: 'https://wa.me/919160006515' },
  { photo: '/about/team/PeeyushKumar.png', name: 'Peeyush Kumar', role: 'Strategic Adviser', bio: 'Peeyush brings insight-driven planning, cross-functional coordination, and long-range thinking to support business innovation and market expansion.', linkedin: 'https://www.linkedin.com/in/peeyushkumar0', email: 'peeyush@prasilabs.com', whatsapp: 'https://wa.me/919160006515' },
  { photo: '/about/team/SheelaYadav.jpg', name: 'Sheela Yadav', role: 'GM — Business Development', bio: 'Sheela excels in building strategic partnerships, driving business growth, and identifying new market opportunities.', linkedin: 'https://www.linkedin.com/in/sheela-yadav-94b957141/', email: 'sheela@prasilabs.com', whatsapp: 'https://wa.me/917400457969' },
  { photo: '/about/team/DeepaliTawade.jpg', name: 'Deepali Tawade', role: 'GM — Product Management', bio: 'Deepali leads product management with a focus on innovation and quality, ensuring successful product launches and market fit.', linkedin: 'https://www.linkedin.com/in/deepali-tawade-2680a22a/', email: 'deepali@prasilabs.com', whatsapp: 'https://wa.me/917400454969' },
  { photo: '/about/team/MVishnuVardhan.jpg', name: 'M. Vishnu Vardhan', role: 'Marketing Manager', bio: 'Vishnu drives marketing strategy, enhancing brand presence and reaching new audiences through targeted campaigns.', linkedin: 'https://www.linkedin.com/in/vishnu-vardhan-011171117/', email: 'marketingmanager@prasilabs.com', whatsapp: 'https://wa.me/919160006515' },
  { photo: '/about/team/Prasad.png', name: 'Dr. B. R V. Prasad Busam', role: 'CRO Head', bio: 'Dr. Prasad ensures operational excellence by aligning research, compliance, and client requirements for high-quality, timely outcomes.', linkedin: 'https://www.linkedin.com/in/prasad-brv-596a321a', email: 'scientist@prasilabs.com', whatsapp: 'https://wa.me/919160006515' },
  { photo: '/about/team/Hassam.png', name: 'Dr. Mohammad Hassam', role: 'Principal Scientist', bio: 'Ensures operational excellence by aligning research, compliance, and client requirements to deliver high-quality and timely outcomes.', linkedin: 'https://www.linkedin.com/in/hassam-mohammad-b9a53325?utm_source=share&utm_campaign=share_via&utm_content=profile&utm_medium=android_app', email: 'labs@prasilabs.com', whatsapp: 'https://wa.me/919160006515' },
  { photo: '/about/team/LatithaSesham.jpg', name: 'Latitha Sesham', role: 'Senior Scientist', bio: "Latitha's research and innovation keep us at the cutting edge of pharmaceutical chemistry and analytical science.", linkedin: 'https://www.linkedin.com/in/lalitha-kumari-sesham-986b7921a/', email: 'labs@prasilabs.com', whatsapp: 'https://wa.me/918688505848' },
];

const VALUES = [
  { color: '#1d4ed8', icon: '💡', title: 'COMMITMENT', text: 'We are committed to providing customers the best-in-class service, pricing, and support while maintaining very high standards in business delivery.' },
  { color: '#7c3aed', icon: '⭐', title: 'CONSISTENCY', text: 'Every order follows our best-in-class processes. With consistent excellence in services and delivery we always endeavor customer satisfaction.' },
  { color: '#059669', icon: '🔍', title: 'TRANSPARENCY', text: 'Our process is open at every step. Stakeholders are always aware of from where, how, and when their products are developed and delivered.' },
];

const CLIENT_LOGOS = [
  { src: '/clients/ajanta-pha-logo.svg', alt: 'Ajanta Pharma', h: 38 },
  { src: '/clients/alembic-Logo.gif', alt: 'Alembic', h: 38 },
  { src: '/clients/amneal-logo.svg', alt: 'Amneal', h: 38 },
  { src: '/clients/biocon-logo-vector.svg', alt: 'Biocon', h: 38 },
  { src: '/clients/Biosys.png', alt: 'Biosys', h: 46 },
  { src: '/clients/Cliantha_logo.png', alt: 'Cliantha', h: 38 },
  { src: '/clients/macleods-logo.jpeg', alt: 'Macleods', h: 38 },
  { src: '/clients/PI-Health-Sciences-Logo.svg', alt: 'PI Health Sciences', h: 38 },
  { src: '/clients/syngene.jpeg', alt: 'Syngene', h: 38 },
  { src: '/clients/aurigene.jpg', alt: 'Aurigene', h: 38 },
];

const SERVICES_LIST = [
  { title: 'Drug Impurities Reference Standards', text: 'A large selection of process impurities, degradation products including compendial impurities listed by USP, EP, and other compendia.' },
  { title: 'Drug Metabolites Reference Standards', text: 'Used in bioanalysis to positively identify and quantitate drug metabolites in blood, urine, and other body fluids.' },
  { title: 'Stable Isotope-Labelled Metabolites', text: 'Deuterium (²H), ¹⁵N, and ¹³C-labeled standards with high chemical and isotopic purity for bioanalytical quantitation.' },
  { title: 'Drug Substance Reference Standards', text: 'High-purity reference standards traceable to compendial references — USP, EP, and BP grade materials.' },
  { title: 'Stable Isotope-Labelled Drug Substances', text: 'Isotopically pure ²H, ¹³C, ¹⁵N-labelled drug substance references with isotopic purity confirmed by high-resolution MS.' },
  { title: 'Fine Labelled Research Chemicals', text: 'Non-drug related research chemicals labelled with stable isotopes, designed to eliminate isotope exchange in demanding research settings.' },
];

/* ─── Sub-components ──────────────────────────────────────── */
function TeamCard({ photo, name, role, bio, linkedin, email, whatsapp }) {
  const [imgErr, setImgErr] = useState(false);
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase();
  const btn = (href, icon, color) => href ? (
    <a href={href} target="_blank" rel="noreferrer"
      style={{ width: 32, height: 32, borderRadius: '50%', background: color + '15', color, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', border: `1px solid ${color}30`, transition: 'background 0.15s' }}
      onMouseEnter={e => e.currentTarget.style.background = color + '28'}
      onMouseLeave={e => e.currentTarget.style.background = color + '15'}
    >{icon}</a>
  ) : null;

  return (
    <div style={{ background: '#fff', borderRadius: 16, overflow: 'hidden', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', transition: 'transform 0.2s, box-shadow 0.2s', display: 'flex', flexDirection: 'column' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(29,78,216,0.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.05)'; }}
    >
      <div style={{ height: 200, background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', overflow: 'hidden', flexShrink: 0 }}>
        {!imgErr
          ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top center' }} onError={() => setImgErr(true)} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#1d4ed8', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', fontWeight: 800 }}>{initials}</div>
          </div>
        }
      </div>
      <div style={{ padding: '18px 18px 16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b', marginBottom: 3 }}>{name}</div>
        <div style={{ fontSize: '0.78rem', color: '#1d4ed8', fontWeight: 600, marginBottom: 10 }}>{role}</div>
        {bio && <p style={{ fontSize: '0.8rem', color: '#64748b', lineHeight: 1.65, marginBottom: 14, flex: 1 }}>{bio}</p>}
        <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #f1f5f9' }}>
          {btn(linkedin, <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" /><circle cx="4" cy="4" r="2" /></svg>, '#0a66c2')}
          {btn(email ? `mailto:${email}` : null, <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>, '#1d4ed8')}
          {btn(whatsapp, <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" /><path d="M12 0C5.373 0 0 5.373 0 12c0 2.127.557 4.124 1.532 5.86L.058 24l6.309-1.648A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" /></svg>, '#16a34a')}
        </div>
      </div>
    </div>
  );
}

/* ─── Journey pit stop card ───────────────────────────────── */
function PitStopCard({ stop, align }) {
  const [hov, setHov] = useState(false);
  return (
    <div onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? stop.bg : '#fff',
        border: `1.5px solid ${hov ? stop.color + '44' : '#e2e8f0'}`,
        borderRadius: 16, padding: '20px 22px',
        boxShadow: hov ? `0 8px 32px ${stop.color}20` : '0 2px 10px rgba(0,0,0,0.05)',
        transition: 'all 0.25s ease',
        textAlign: align,
        maxWidth: 380,
        marginLeft: align === 'left' ? 0 : 'auto',
        marginRight: align === 'right' ? 0 : 'auto',
      }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: stop.color, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>
        {stop.label}
      </div>
      <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#1e293b', marginBottom: 8, lineHeight: 1.3 }}>{stop.title}</div>
      <div style={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.75 }}>{stop.desc}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════ */
export default function About() {
  return (
    <>
      <SEO
        title="About PRASI LABS — Pharma Reference Standards Manufacturer, Hyderabad"
        description="PRASI LABS Pvt Limited is a Hyderabad-based manufacturer and supplier of pharmaceutical reference standards, drug impurities, and custom synthesis services since 2020. ISO compliant, USP/EP/BP grade standards."
        keywords="about PRASI LABS, pharma company Hyderabad, reference standards manufacturer India, drug impurity supplier Telangana, custom synthesis company, CRO services India, pharmaceutical standards manufacturer"
        canonical="/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About PRASI LABS',
          url: 'https://www.prasilabs.com/about',
          description: 'PRASI LABS is a Hyderabad-based pharmaceutical reference standards and drug impurities supplier, offering custom synthesis, CRO, and method development services.',
          publisher: { '@id': 'https://www.prasilabs.com/#organization' },
        }}
      />
      <div>
        <style>{`
        @keyframes float { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes spin-slow { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
        @keyframes road-pulse { 0%,100% { opacity:0.6; } 50% { opacity:1; } }
        @keyframes ticker-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .clients-ticker { display: flex; align-items: center; gap: 72px; animation: ticker-scroll 30s linear infinite; width: max-content; }
        .clients-ticker:hover { animation-play-state: paused; }
        .pitstop-circle { transition: transform 0.2s, box-shadow 0.2s; }
        .pitstop-circle:hover { transform: scale(1.12); }
        @media (max-width: 700px) {
          .journey-row { flex-direction: column !important; }
          .journey-left, .journey-right { width: 100% !important; text-align: left !important; }
          .journey-left { order: 2; }
          .journey-center { order: 1; align-self: flex-start; flex-direction: row !important; margin-bottom: 16px; }
          .road-line { display: none; }
        }
      `}</style>

        {/* ── 1. HERO ──────────────────────────────────────────── */}
        <section style={{ position: 'relative', minHeight: 480, overflow: 'hidden', display: 'flex', alignItems: 'center' }}>
          <img src="/about/hero.jpg" alt="PRASI LABS" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(10,22,40,0.92), rgba(29,78,216,0.70))' }} />

          {/* DNA strand — left decoration */}
          <DNAStrand size={80} height={300} style={{ position: 'absolute', left: '5%', top: '50%', transform: 'translateY(-50%)', opacity: 0.13, pointerEvents: 'none' }} />

          {/* Animated molecule decoration */}
          <svg style={{ position: 'absolute', right: '8%', top: '50%', transform: 'translateY(-50%)', opacity: 0.08, animation: 'spin-slow 30s linear infinite' }} width="280" height="280" viewBox="0 0 280 280">
            <circle cx="140" cy="140" r="120" stroke="white" strokeWidth="1.5" fill="none" strokeDasharray="6 8" />
            <circle cx="140" cy="30" r="20" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.2" />
            <circle cx="240" cy="90" r="16" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.15" />
            <circle cx="240" cy="190" r="20" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.2" />
            <circle cx="140" cy="250" r="16" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.15" />
            <circle cx="40" cy="190" r="20" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.2" />
            <circle cx="40" cy="90" r="16" stroke="white" strokeWidth="2" fill="white" fillOpacity="0.15" />
            <line x1="140" y1="50" x2="225" y2="103" stroke="white" strokeWidth="2" />
            <line x1="224" y1="110" x2="224" y2="175" stroke="white" strokeWidth="2" />
            <line x1="225" y1="177" x2="140" y2="234" stroke="white" strokeWidth="2" />
            <line x1="140" y1="234" x2="55" y2="177" stroke="white" strokeWidth="2" />
            <line x1="56" y1="175" x2="56" y2="110" stroke="white" strokeWidth="2" />
            <line x1="55" y1="103" x2="140" y2="50" stroke="white" strokeWidth="2" />
          </svg>

          <div style={{ position: 'relative', maxWidth: 800, margin: '0 auto', padding: '100px 28px 90px', textAlign: 'center', zIndex: 2 }}>
            <FadeIn direction="down" delay={0}>
              <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)', fontSize: '0.72rem', fontWeight: 700, padding: '5px 18px', borderRadius: 20, marginBottom: 22, border: '1px solid rgba(255,255,255,0.22)', letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                About Us · Founded {COMPANY.founded} · Hyderabad, India
              </div>
            </FadeIn>
            <FadeIn direction="up" delay={120}>
              <h1 style={{ color: '#fff', fontSize: 'clamp(2rem, 4.5vw, 3.2rem)', fontWeight: 900, marginBottom: 18, lineHeight: 1.15, letterSpacing: '-0.5px' }}>
                Pioneering Pharmaceutical<br />
                <span style={{ background: 'linear-gradient(90deg,#60a5fa,#34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  Chemistry Since 2020
                </span>
              </h1>
            </FadeIn>
            <FadeIn direction="up" delay={220}>
              <p style={{ color: 'rgba(255,255,255,0.84)', fontSize: '1.05rem', lineHeight: 1.85, maxWidth: 620, margin: '0 auto 32px' }}>
                India's trusted supplier of pharmaceutical drug impurities, stable isotope-labelled compounds, and custom synthesis solutions — serving global pharma and CRO clients with transparency and precision.
              </p>
            </FadeIn>
            <FadeIn direction="up" delay={320}>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 14, flexWrap: 'wrap' }}>
                <Link to="/catalog" style={{ padding: '12px 30px', background: '#fff', color: '#1d4ed8', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>Browse Catalog</Link>
                <Link to="/contact" style={{ padding: '12px 30px', background: 'rgba(255,255,255,0.12)', color: '#fff', borderRadius: 10, fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem', border: '1.5px solid rgba(255,255,255,0.3)' }}>Talk to Our Scientists</Link>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── 2. QUICK STATS ───────────────────────────────────── */}
        <section style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '0 24px' }}>
          <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', flexWrap: 'wrap', justifyContent: 'center' }}>
            {[
              { value: '2020', label: 'Year Founded' },
              { value: '1,500+', label: 'Catalog Items' },
              { value: '≥ 98%', label: 'Purity Guaranteed' },
              { value: '50+', label: 'Global Clients' },
              { value: '8+', label: 'ISO Accreditations' },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <div style={{ padding: '26px 28px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#1d4ed8', letterSpacing: '-0.5px', lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: 5 }}>{s.label}</div>
                </div>
                {i < arr.length - 1 && <div style={{ width: 1, background: '#e2e8f0', margin: '18px 0' }} />}
              </React.Fragment>
            ))}
          </div>
        </section>

        {/* ── 3. OUR STORY ─────────────────────────────────────── */}
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(44px,8vw,80px) clamp(16px,3vw,24px)' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 60, alignItems: 'center' }}>
            <FadeIn direction="left">
              <div>
                <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 16, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Our Story</div>
                <h2 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#1e293b', marginBottom: 20, lineHeight: 1.25 }}>
                  Custom Synthesis &amp; Drug Impurities — Built for the World
                </h2>
                <p style={{ color: '#475569', fontSize: '0.96rem', lineHeight: 1.85, marginBottom: 16 }}>
                  PRASI LABS was founded in 2020 with a mission to make high-quality pharmaceutical reference standards accessible and transparent. Starting from a small team in Hyderabad, we have grown into a globally trusted supplier.
                </p>
                <p style={{ color: '#475569', fontSize: '0.96rem', lineHeight: 1.85, marginBottom: 28 }}>
                  We bring value through a new, transparent way of sourcing pharmaceutical impurities and custom synthesis — where every compound is backed by solid analytical data and every delivery is traceable.
                </p>
                <div style={{ display: 'flex', gap: 12 }}>
                  <Link to="/contact" style={{ padding: '11px 26px', background: '#1d4ed8', color: '#fff', borderRadius: 9, fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem' }}>Contact Us</Link>
                  <Link to="/catalog" style={{ padding: '11px 26px', background: '#eff6ff', color: '#1d4ed8', borderRadius: 9, fontWeight: 700, textDecoration: 'none', fontSize: '0.9rem' }}>View Catalog</Link>
                </div>
              </div>
            </FadeIn>
            <FadeIn direction="right" delay={100}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  { icon: '🔬', value: 'mg → kg', label: 'Synthesis Scale' },
                  { icon: '🌍', value: 'Global', label: 'Export Reach' },
                  { icon: '📜', value: 'ICH·USP·EP', label: 'Standards Followed' },
                  { icon: '⚡', value: '1–3 Weeks', label: 'Lead Time' },
                ].map(s => (
                  <div key={s.label} style={{ background: 'linear-gradient(135deg,#eff6ff,#dbeafe)', borderRadius: 14, padding: '22px 18px', textAlign: 'center', border: '1px solid #bfdbfe' }}>
                    <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>{s.icon}</div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#1d4ed8', lineHeight: 1 }}>{s.value}</div>
                    <div style={{ color: '#475569', fontSize: '0.78rem', marginTop: 5 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── 4. JOURNEY — ROAD / PIT STOPS ───────────────────── */}
        <section style={{ background: 'linear-gradient(180deg,#f8fafc 0%,#fff 100%)', borderTop: '1px solid #e2e8f0', padding: 'clamp(44px,8vw,80px) 0 clamp(56px,10vw,100px)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 24px' }}>
            <FadeIn>
              <div style={{ textAlign: 'center', marginBottom: 64 }}>
                <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Our Journey</div>
                <h2 style={{ fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 900, color: '#1e293b', marginBottom: 12, letterSpacing: '-0.5px' }}>
                  Road from Inception to Excellence
                </h2>
                <p style={{ fontSize: '1rem', color: '#64748b', maxWidth: 520, margin: '0 auto', lineHeight: 1.75 }}>
                  Every milestone on our journey — from a bold idea in Hyderabad to serving pharma companies across four continents.
                </p>
              </div>
            </FadeIn>

            {/* Road container */}
            <div style={{ position: 'relative' }}>
              {/* Center road line */}
              <div className="road-line" style={{
                position: 'absolute', left: '50%', top: 28, bottom: 28, width: 6, transform: 'translateX(-50%)', zIndex: 0,
                background: 'repeating-linear-gradient(to bottom, #1d4ed8 0, #1d4ed8 20px, #bfdbfe 20px, #bfdbfe 36px)',
                borderRadius: 4, opacity: 0.6, animation: 'road-pulse 3s ease-in-out infinite',
              }} />

              {JOURNEY.map((stop, i) => {
                const isLeft = i % 2 === 0;
                return (
                  <FadeIn key={i} direction={isLeft ? 'left' : 'right'} delay={i * 80}>
                    <div className="journey-row" style={{ display: 'flex', alignItems: 'center', marginBottom: 40, position: 'relative', zIndex: 1 }}>

                      {/* Left slot */}
                      <div className="journey-left" style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', paddingRight: 32 }}>
                        {isLeft ? <PitStopCard stop={stop} align="right" /> : null}
                      </div>

                      {/* Center: pit stop */}
                      <div className="journey-center" style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 100 }}>
                        <div className="pitstop-circle" style={{
                          width: 62, height: 62, borderRadius: '50%',
                          background: `linear-gradient(135deg, ${stop.color}, ${stop.color}cc)`,
                          border: '4px solid #fff',
                          boxShadow: `0 0 0 5px ${stop.color}30, 0 8px 24px ${stop.color}44`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: '1.6rem', zIndex: 2, position: 'relative', cursor: 'default',
                        }}>
                          {stop.icon}
                        </div>
                        <div style={{
                          padding: '3px 12px', background: stop.color, color: '#fff',
                          borderRadius: 20, fontSize: '0.75rem', fontWeight: 800,
                          boxShadow: `0 3px 10px ${stop.color}50`,
                          whiteSpace: 'nowrap',
                        }}>
                          {stop.year}
                        </div>
                        {/* connector to next */}
                        {i < JOURNEY.length - 1 && (
                          <div style={{ width: 2, height: 32, background: `linear-gradient(to bottom, ${stop.color}60, ${JOURNEY[i + 1].color}60)`, borderRadius: 2 }} />
                        )}
                      </div>

                      {/* Right slot */}
                      <div className="journey-right" style={{ flex: 1, paddingLeft: 32 }}>
                        {!isLeft ? <PitStopCard stop={stop} align="left" /> : null}
                      </div>
                    </div>
                  </FadeIn>
                );
              })}

              {/* Finish flag */}
              <FadeIn delay={JOURNEY.length * 80}>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8, position: 'relative', zIndex: 999 }}>
                  <div style={{ background: 'linear-gradient(135deg,#0f172a,#1d4ed8)', borderRadius: 14, padding: '16px 32px', display: 'flex', alignItems: 'center', gap: 12, color: '#fff', boxShadow: '0 8px 28px rgba(29,78,216,0.3)', position: 'relative', zIndex: 999 }}>
                    <span style={{ fontSize: '1.4rem' }}>🏁</span>
                    <div>
                      <div style={{ fontWeight: 800, fontSize: '1rem' }}>Today & Beyond</div>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)' }}>Continuing to innovate for global pharma</div>
                    </div>
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* ── 5. OUR VALUES ────────────────────────────────────── */}
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(44px,8vw,80px) clamp(16px,3vw,24px)' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, letterSpacing: '0.5px', textTransform: 'uppercase' }}>What We Stand For</div>
              <h2 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#1e293b', marginBottom: 12 }}>Our Core Values</h2>
              <p style={{ color: '#64748b', maxWidth: 560, margin: '0 auto', fontSize: '0.95rem', lineHeight: 1.8 }}>
                Our values of commitment, consistency, and transparency drive every decision — from lab synthesis to global delivery.
              </p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
            {VALUES.map((v, i) => (
              <FadeIn key={v.title} direction="up" delay={i * 100}>
                <div style={{ background: '#fff', borderRadius: 18, padding: '36px 28px', textAlign: 'center', border: `2px solid ${v.color}20`, boxShadow: '0 2px 12px rgba(0,0,0,0.05)', transition: 'all 0.25s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = v.color + '55'; e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = `0 16px 40px ${v.color}20`; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = v.color + '20'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.05)'; }}
                >
                  <div style={{ fontSize: '2.8rem', marginBottom: 16 }}>{v.icon}</div>
                  <h3 style={{ fontSize: '1rem', fontWeight: 800, color: '#1e293b', marginBottom: 12, letterSpacing: '0.5px' }}>{v.title}</h3>
                  <div style={{ width: 40, height: 3, background: v.color, borderRadius: 2, margin: '0 auto 14px' }} />
                  <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.8 }}>{v.text}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </section>

        {/* ── Our Clients ──────────────────────────────────── */}
        <section style={{ background: '#fff', borderTop: '1px solid #e2e8f0', borderBottom: '1px solid #e2e8f0', padding: 'clamp(40px,6vw,64px) 0', overflow: 'hidden' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 40, padding: '0 24px' }}>
              <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Trusted Industry Partnership</div>
              <h2 style={{ fontSize: '1.7rem', fontWeight: 900, color: '#1e293b', marginBottom: 8, letterSpacing: '-0.4px' }}>Trusted by Leading Pharma Companies</h2>
              <p style={{ fontSize: '0.9rem', color: '#64748b' }}>Clients across India, Europe, the US, and Asia-Pacific</p>
            </div>
          </FadeIn>
          <div style={{ overflow: 'hidden', width: '100%' }}>
            <div className="clients-ticker">
              {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((logo, i) => (
                <div key={i} style={{ flexShrink: 0, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={logo.src} alt={logo.alt}
                    style={{ height: logo.h, width: 'auto', maxWidth: 160, objectFit: 'contain', filter: 'grayscale(30%)', opacity: 0.78, transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.filter = 'grayscale(0%)'; e.currentTarget.style.opacity = '1'; }}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'grayscale(30%)'; e.currentTarget.style.opacity = '0.78'; }}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── 6. CUSTOM SYNTHESIS ──────────────────────────────── */}
        <section style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a8a)', padding: 'clamp(40px,6vw,72px) clamp(16px,3vw,24px)', position: 'relative', overflow: 'hidden' }}>
          <svg style={{ position: 'absolute', left: 40, top: '50%', transform: 'translateY(-50%)', opacity: 0.05 }} width="260" height="240" viewBox="0 0 260 240">
            <circle cx="65" cy="120" r="34" stroke="white" strokeWidth="3" fill="none" />
            <circle cx="130" cy="40" r="34" stroke="white" strokeWidth="3" fill="none" />
            <circle cx="195" cy="120" r="34" stroke="white" strokeWidth="3" fill="none" />
            <circle cx="130" cy="200" r="34" stroke="white" strokeWidth="3" fill="none" />
            <line x1="96" y1="97" x2="109" y2="67" stroke="white" strokeWidth="3" />
            <line x1="151" y1="67" x2="164" y2="97" stroke="white" strokeWidth="3" />
            <line x1="164" y1="143" x2="151" y2="173" stroke="white" strokeWidth="3" />
            <line x1="109" y1="173" x2="96" y2="143" stroke="white" strokeWidth="3" />
          </svg>
          <FadeIn>
            <div style={{ maxWidth: 820, margin: '0 auto', textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <div style={{ display: 'inline-block', background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 20, padding: '4px 16px', fontSize: '0.72rem', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 20 }}>Tailored Solutions</div>
              <h2 style={{ color: '#fff', fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 900, marginBottom: 16, letterSpacing: '-0.4px' }}>
                Tailored Custom Synthesis Solutions
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', lineHeight: 1.9 }}>
                We offer custom synthesis up to gram-quantities for industrial and academic customers worldwide. We listen closely and provide products meeting your exact specifications — purity supported by solid analytical data (HPLC, NMR, LC-MS, IR), certification tailored to your needs, and a strong partner network enabling fast turnaround beyond your expectations.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* ── 7. SERVICES WE OFFER ─────────────────────────────── */}
        <section style={{ background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: 'clamp(44px,8vw,80px) clamp(16px,3vw,24px)' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <FadeIn>
              <div style={{ textAlign: 'center', marginBottom: 48 }}>
                <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Specialisations</div>
                <h2 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>What We Offer</h2>
                <p style={{ color: '#64748b', fontSize: '0.95rem' }}>Comprehensive pharmaceutical reference standard solutions</p>
              </div>
            </FadeIn>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 22 }}>
              {SERVICES_LIST.map((svc, i) => (
                <FadeIn key={i} direction="up" delay={i * 70}>
                  <div style={{ background: '#fff', borderRadius: 14, padding: '26px 24px', border: '1px solid #e2e8f0', height: '100%', boxSizing: 'border-box', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(29,78,216,0.1)'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#1d4ed8', flexShrink: 0 }} />
                      <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>{svc.title}</h4>
                    </div>
                    <div style={{ width: 36, height: 2, background: '#dbeafe', borderRadius: 1, marginBottom: 12 }} />
                    <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.78, margin: 0 }}>{svc.text}</p>
                  </div>
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ── 8. TEAM ──────────────────────────────────────────── */}
        <section style={{ maxWidth: 1100, margin: '0 auto', padding: 'clamp(44px,8vw,80px) clamp(16px,3vw,24px)' }}>
          <FadeIn>
            <div style={{ textAlign: 'center', marginBottom: 48 }}>
              <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.72rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Our People</div>
              <h2 style={{ fontSize: '1.85rem', fontWeight: 800, color: '#1e293b', marginBottom: 10 }}>Meet the Team</h2>
              <p style={{ color: '#64748b', fontSize: '0.95rem' }}>Experienced professionals across pharmaceutical sciences, chemistry, and business development</p>
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 22 }}>
            {TEAM.map((m, i) => (
              <FadeIn key={m.name} direction="up" delay={i * 60}>
                <TeamCard {...m} />
              </FadeIn>
            ))}
          </div>
        </section>

        {/* ── 9. ACCREDITATIONS ────────────────────────────────── */}
        <section style={{ background: '#fde8e8', padding: 'clamp(44px,8vw,80px) clamp(16px,3vw,24px)', borderTop: '1px solid #fca5a5' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
            <FadeIn>
              <h2 style={{ fontSize: '2rem', fontWeight: 900, color: '#1e293b', marginBottom: 10 }}>Accreditations</h2>
              <p style={{ fontSize: '1rem', color: '#475569', marginBottom: 48 }}>Our quality is validated by internationally recognized certifications</p>
            </FadeIn>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 20 }}>
              {ACCREDITATIONS.map((acc, i) => (
                <FadeIn key={acc.alt} direction="up" delay={i * 50}>
                  <div style={{ background: '#fff', borderRadius: 14, padding: 12, boxShadow: '0 2px 14px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 116, height: 116, transition: 'transform 0.2s, box-shadow 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.08)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.14)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 14px rgba(0,0,0,0.08)'; }}
                  >
                    <img src={acc.src} alt={acc.alt} style={{ maxWidth: 92, maxHeight: 92, objectFit: 'contain', borderRadius: 6 }} />
                  </div>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={400}>
              <div style={{ marginTop: 36, fontSize: '0.8rem', color: '#64748b', lineHeight: 1.8 }}>
                CIN: {COMPANY.cin} &nbsp;·&nbsp; GST: {COMPANY.gstin} &nbsp;·&nbsp; IEC: {COMPANY.iec} &nbsp;·&nbsp; MSME: {COMPANY.msme}
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── Lab & Facility ───────────────────────────────────── */}
        <section style={{ padding: 'clamp(48px,8vw,88px) clamp(16px,3vw,24px)', background: '#f8fafc' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
            <FadeIn>
              <div style={{ textAlign: 'center', marginBottom: 52 }}>
                <div style={{ display: 'inline-block', background: '#eff6ff', color: '#1d4ed8', fontSize: '0.7rem', fontWeight: 700, padding: '4px 14px', borderRadius: 20, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Our Lab</div>
                <h2 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 900, color: '#0f172a', marginBottom: 14, letterSpacing: '-0.4px' }}>State-of-the-Art Facility</h2>
                <p style={{ fontSize: '1rem', color: '#475569', maxWidth: 520, margin: '0 auto', lineHeight: 1.75 }}>
                  Our Hyderabad laboratory is equipped with advanced synthesis and analytical instrumentation to deliver compounds of exceptional quality.
                </p>
              </div>
            </FadeIn>

            {/* Equipment grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))', gap: 20, marginBottom: 52 }}>
              {[
                { icon: '🔬', title: 'HPLC Systems', desc: 'High-performance liquid chromatography for purity analysis and method development.' },
                { icon: '⚗️', title: 'NMR Spectroscopy', desc: 'Structural confirmation of synthesized compounds at mg to gram scale.' },
                { icon: '🧪', title: 'LC-MS', desc: 'Liquid chromatography–mass spectrometry for molecular weight and fragmentation analysis.' },
                { icon: '🏭', title: 'Synthesis Reactors', desc: 'Bench-scale fume hoods, rotary evaporators, jacketed reactors, and vacuum distillation.' },
                { icon: '❄️', title: 'Cold Storage', desc: '-20°C and -80°C storage for sensitive standards and unstable intermediates.' },
                { icon: '📋', title: 'QC Laboratory', desc: 'Dedicated QC lab for IR, melting point, and lot-specific release testing.' },
              ].map((eq, i) => (
                <FadeIn key={eq.title} delay={i * 60} direction="up">
                  <div style={{ background: '#fff', borderRadius: 16, padding: '24px 22px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.04)', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 28px rgba(29,78,216,0.1)'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.04)'; e.currentTarget.style.transform = 'none'; }}>
                    <div style={{ fontSize: '2rem', marginBottom: 14 }}>{eq.icon}</div>
                    <div style={{ fontWeight: 800, fontSize: '0.95rem', color: '#0f172a', marginBottom: 8 }}>{eq.title}</div>
                    <div style={{ fontSize: '0.83rem', color: '#475569', lineHeight: 1.7 }}>{eq.desc}</div>
                  </div>
                </FadeIn>
              ))}
            </div>

            {/* Location & working environment */}
            <FadeIn delay={100}>
              <div className="pub-lab-card" style={{ background: 'linear-gradient(135deg, #0a1628, #1e3a8a)', borderRadius: 20, padding: 'clamp(24px,4vw,36px) clamp(20px,4vw,40px)', color: '#fff' }}>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#93c5fd', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12 }}>Facility Location</div>
                  <h3 style={{ fontSize: '1.3rem', fontWeight: 900, marginBottom: 12, lineHeight: 1.3 }}>TSIIC Industrial Estate, Kukatpally, Hyderabad</h3>
                  <p style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.75, marginBottom: 0 }}>
                    Strategically located in Telangana's pharmaceutical corridor — close to Genome Valley and major pharma clusters. Our 2,000 sq ft facility operates under MSME registration with all necessary licenses for synthesis and export.
                  </p>
                </div>
                <div className="pub-lab-stats">
                  {[
                    { label: 'Lab Area', value: '2,000 sq ft' },
                    { label: 'Synthesis Capacity', value: 'mg to 1 kg' },
                    { label: 'Annual Output', value: '1,000+ batches' },
                    { label: 'Export Countries', value: '30+' },
                  ].map(s => (
                    <div key={s.label} style={{ textAlign: 'center', background: 'rgba(255,255,255,0.08)', borderRadius: 12, padding: '12px 20px', border: '1px solid rgba(255,255,255,0.12)' }}>
                      <div style={{ fontWeight: 900, fontSize: '1.3rem', color: '#60a5fa' }}>{s.value}</div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── 10. CTA ──────────────────────────────────────────── */}
        <section style={{ background: 'linear-gradient(135deg,#0f172a,#1d4ed8)', padding: 'clamp(44px,8vw,80px) clamp(16px,3vw,24px)', textAlign: 'center' }}>
          <FadeIn>
            <div style={{ maxWidth: 560, margin: '0 auto' }}>
              <h2 style={{ color: '#fff', fontSize: 'clamp(1.6rem,3vw,2.2rem)', fontWeight: 900, marginBottom: 16, letterSpacing: '-0.4px' }}>Ready to Work Together?</h2>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '1rem', marginBottom: 36, lineHeight: 1.8 }}>
                Contact our team to discuss your specific requirements and get a customized quote within 24 hours.
              </p>
              <Link to="/contact" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '14px 36px', background: '#fff', color: '#1d4ed8', borderRadius: 10, fontWeight: 800, textDecoration: 'none', fontSize: '0.95rem', boxShadow: '0 4px 20px rgba(0,0,0,0.2)' }}>
                Get in Touch
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </Link>
            </div>
          </FadeIn>
        </section>
      </div>
    </>
  );
}
